import * as Sentry from '@sentry/vue';

// Browser page translation (Chrome's Google Translate, Yandex, Naver Whale, the
// Google iOS app) rewrites the document underneath us: every meaningful text
// node is replaced by nested <font> wrappers. Vue keeps references to the
// original text nodes and uses them as insertion anchors, so the next patch that
// mounts an element in front of a translated-away text node calls
// `parent.insertBefore(child, detachedTextNode)` and the browser throws
// NotFoundError (DNDPLAYBOOK-R6, DNDPLAYBOOK-TV).
//
// That throw aborts the render effect mid-patch. Sentry installs an
// `app.config.errorHandler`, so it is swallowed rather than propagated, leaving
// `instance.subTree` pointing at a half-built tree whose vnodes never got their
// `el`/`component` assigned. Every later patch then trips over that corruption —
// which is where "reading 'emitsOptions'" (VK), "destructure property 'bum'"
// (W8) and "reading 'parentNode'" (SA/W9) come from. The user sees a white
// screen and has to reload, losing whatever the page was showing.
//
// Falling back to an append keeps the tree patchable instead. The node can land
// in the wrong position, but by then the translator has already reordered the
// page, and a misplaced node beats a dead tab on the artwork tab where the
// generation controls live.
//
// The fallback is reported once per page load so a detached anchor caused by our
// own code — rather than by a translator — still surfaces instead of being
// silently papered over. `translated` tells the two apart.

let reported = false;

const looksTranslated = (): boolean => {
  const root = document.documentElement;

  return (
    root.classList.contains('translated-ltr') ||
    root.classList.contains('translated-rtl') ||
    document.querySelector('font[style*="vertical-align"]') !== null
  );
};

/**
 * Make `Node.insertBefore` tolerate an anchor that a third party has detached,
 * appending instead of throwing. Browser-only; the SSR entrypoint never calls it.
 */
export const installTolerantInsertBefore = (): void => {
  if (typeof Node === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const original = Node.prototype.insertBefore;

  Node.prototype.insertBefore = function <T extends Node>(node: T, child: Node | null): T {
    if (child && child.parentNode !== this) {
      if (!reported) {
        reported = true;

        Sentry.captureMessage('insertBefore anchor was detached; appended instead', {
          level: 'warning',
          extra: { translated: looksTranslated() },
        });
      }

      return original.call(this, node, null) as T;
    }

    return original.call(this, node, child) as T;
  };
};
